import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import notify from 'components/ui/notify'
import { getUserDetails, updateUser } from 'slices/authSlice'
import { usersService } from 'services'
import { Form, useForm } from 'components/controls/useForm'
import UserUpdatePassword from 'components/UserUpdatePassword'
import TWControls from 'components/controls/TWControls'
import { SingleImageUploader } from 'components/FileUploader'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { KeyRound } from 'lucide-react'
import { cn } from 'lib/utils'

export interface UserProfileFormProps {
	/** Called after a successful save, so a popup can close itself. */
	onSaved?: () => void
	/**
	 * Stack the fields and shrink the avatar. The form is laid out in rows of
	 * three for a 1080px card, which is unusable in a 390px dialog.
	 */
	compact?: boolean
	className?: string
}

/**
 * The user's own profile, without any chrome around it.
 *
 * Extracted from `UserProfileCN` so the same form can be a full screen at a
 * desk and a popup on a phone. It was the screen: the route rendered the admin
 * sidebar, the header and this form, and reaching it from the field app would
 * have ejected the user into desktop chrome they had deliberately left.
 *
 * Everything here came from that screen unchanged, including the password
 * dialog, which belongs with the form rather than with whatever is hosting it.
 */
export function UserProfileForm({ onSaved, compact = false, className }: UserProfileFormProps) {
	const dispatch = useDispatch()

	const { userInfo, isInitialized } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	useEffect(() => {
		if (!isInitialized) {
			// @ts-ignore
			dispatch(getUserDetails(userInfo._id))
		}
	}, [dispatch, userInfo, isInitialized])

	const { values, setValues, handleInputChange } = useForm(userInfo)

	const [image, setImage] = useState(userInfo?.image)
	const [openPopup, setOpenPopup] = useState(false)
	const [passwordError, setPasswordError] = useState('')

	const submitForm = async (vals: any) => {
		// @ts-ignore
		await dispatch(
			// @ts-ignore
			updateUser({
				_id: userId,
				image: vals.image,
				name: vals.name,
				firstName: vals.firstName,
				email: vals.email,
				company: vals.company,
				phone: vals.phone,
				title: vals.title,
				role: vals.role,
				firstTime: vals.firstTime,
				isCompleted: vals.isCompleted,
				linkedin: vals.linkedin,
				instagram: vals.instagram,
				facebook: vals.facebook,
				twitter: vals.twitter,
			}),
		)

		notify({
			text: 'Profile updated',
			icon: 'success',
			buttons: {
				confirm: {
					text: 'Ok',
					value: true,
					visible: true,
					className: '',
					closeModal: true,
				},
			},
		})

		onSaved?.()
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (!openPopup) {
			submitForm(values)
		}
	}

	const uploadImage = async (fileInfo: any) => {
		const fileUrl = fileInfo?.originalUrl ?? fileInfo?.cdnUrl ?? ''
		if (!fileUrl) return

		setImage(fileUrl)
		const newValues = { ...values, image: fileUrl }
		setValues(newValues)
		submitForm(newValues)
	}

	const updatePassword = async (passwordData: any, resetForm: () => void) => {
		let data
		try {
			data = await usersService().updatePassword({
				oldPassword: passwordData.oldPassword,
				newPassword: passwordData.newPassword,
			})
		} catch (error: any) {
			// Report what actually failed. This previously swallowed every error
			// into "Incorrect password", which hid the real cause: an expired
			// session, a validation rejection, or the server being down.
			setPasswordError(
				error?.response?.data?.message || error?.message || 'Could not update password.',
			)
			return
		}

		if (data?.success) {
			notify({
				title: `Password Updated!`,
				text: `Your password was successfully updated`,
				icon: 'success',
				buttons: {
					confirm: {
						text: 'Ok',
						value: true,
						visible: true,
						className: '',
						closeModal: true,
					},
				},
			})

			resetForm()
			setOpenPopup(false)
		} else {
			setPasswordError('Could not update password. Please try again.')
		}
	}

	/** Rows of three at a desk, one column in a dialog. */
	const row = compact ? 'flex flex-col gap-2 mb-2' : 'flex flex-col sm:flex-row gap-2 mb-2'

	return (
		<div className={className}>
			<Form onSubmit={handleSubmit}>
				<div
					className={cn(
						'mb-6 flex items-start gap-8',
						compact ? 'flex-col-reverse items-center' : 'flex-col md:flex-row',
					)}
				>
					{/* Personal Details */}
					<div className="flex w-full flex-1 flex-col">
						<h3 className="mt-2 mb-2 pb-2 text-sm font-normal text-gray-500">Personal Details</h3>
						<div className={row}>
							<TWControls.Input
								placeholder="Full Name"
								size="small"
								name="name"
								label="Full Name"
								value={values.name}
								onChange={handleInputChange}
								fullWidth
							/>
							<TWControls.Input
								placeholder="First Name"
								size="small"
								name="firstName"
								label="First Name"
								value={values.firstName}
								onChange={handleInputChange}
								fullWidth
							/>
						</div>
						<TWControls.Input
							placeholder="Email"
							size="small"
							name="email"
							label="Email"
							value={values.email}
							onChange={handleInputChange}
							fullWidth
						/>
						<TWControls.Input
							placeholder="Phone"
							size="small"
							name="phone"
							label="Phone"
							value={values.phone}
							onChange={handleInputChange}
							fullWidth
						/>
					</div>

					{/* Avatar Uploader */}
					<div
						className={cn(
							'flex w-full flex-col items-center justify-center md:w-auto',
							compact ? 'pt-2' : 'pt-8',
						)}
					>
						<SingleImageUploader
							value={image}
							onChange={uploadImage}
							size={compact ? 112 : 180}
							description={compact ? 'Tap to update your photo' : 'Drag & drop or click to update your photo'}
						/>
					</div>
				</div>

				<h3 className="mt-2 mb-2 pb-2 text-sm font-normal text-gray-500">Employment Details</h3>
				<div className={row}>
					<TWControls.Input
						placeholder="Company"
						size="small"
						name="company"
						label="Company"
						value={values.company}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Title"
						size="small"
						name="title"
						label="Title"
						value={values.title}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Role"
						size="small"
						name="role"
						label="Role"
						value={values.role}
						onChange={handleInputChange}
						fullWidth
					/>
				</div>

				<h3 className="mt-2 mb-2 pb-2 text-sm font-normal text-gray-500">Social Media</h3>
				<div className={row}>
					<TWControls.Input
						placeholder="LinkedIn"
						size="small"
						name="linkedin"
						label="LinkedIn"
						value={values.linkedin}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Instagram"
						size="small"
						name="instagram"
						label="Instagram"
						value={values.instagram}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Facebook"
						size="small"
						name="facebook"
						label="Facebook"
						value={values.facebook}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Twitter"
						size="small"
						name="twitter"
						label="X (Twitter)"
						value={values.twitter}
						onChange={handleInputChange}
						fullWidth
					/>
				</div>

				<div className="mt-4 flex flex-row gap-4">
					<button
						type="button"
						onClick={() => setOpenPopup(true)}
						className="rounded-lg border border-[#0080fe] bg-white px-4 py-2 text-sm font-medium text-[#0080fe] transition-colors hover:bg-[#0080fe]/5"
					>
						Change Password
					</button>
					<button
						type="submit"
						className="rounded-lg bg-[#0080fe] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#0070e0]"
					>
						Update
					</button>
				</div>
			</Form>

			{/* Change Password Dialog */}
			<Dialog open={openPopup} onClose={() => setOpenPopup(false)}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0080fe]/10 text-[#0080fe]">
								<KeyRound className="h-5 w-5" />
							</div>
							<div>
								<DialogTitle>Change Password</DialogTitle>
								<DialogDescription>Update your account password</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<UserUpdatePassword
							updatePassword={updatePassword}
							externalError={passwordError}
							setExternalError={setPasswordError}
						/>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default UserProfileForm
